/**
 * 分镜看板相关 API Hooks
 */
import {
  keepPreviousData,
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { storyboardBoardApi } from '@/services/api';
import type {
  StoryboardBoardTrackWithItems,
  StoryboardBoardItem,
  StoryboardBoardPaginatedData,
} from '@/types';
import { clearStoryboardBoardCache } from '@/lib/storyboard/board-cache';
import { shouldKeepStoryboardWindowPlaceholderData } from '@/lib/storyboard-board/project-switch';

// Query Keys
export const storyboardBoardKeys = {
  all: ['storyboardBoard'] as const,
  lists: () => [...storyboardBoardKeys.all, 'list'] as const,
  list: (projectId: string, trackTypesKey = 'all') =>
    [...storyboardBoardKeys.lists(), projectId, trackTypesKey] as const,
  skeletons: () => [...storyboardBoardKeys.all, 'skeleton'] as const,
  skeleton: (projectId: string, trackTypesKey = 'default') =>
    [...storyboardBoardKeys.skeletons(), projectId, trackTypesKey] as const,
  paginateds: () => [...storyboardBoardKeys.all, 'paginated'] as const,
  paginated: (projectId: string, startIndex: number, endIndex: number, trackTypesKey = 'default') =>
    [...storyboardBoardKeys.paginateds(), projectId, startIndex, endIndex, trackTypesKey] as const,
};

const isStoryboardBoardProjectQuery = (
  queryKey: readonly unknown[],
  type: 'list' | 'skeleton' | 'paginated',
  projectId: string
) => queryKey[0] === 'storyboardBoard' && queryKey[1] === type && queryKey[2] === projectId;

const createStoryboardBoardPlaceholderData =
  <T>(projectId: string | null) =>
  (previousData: T | undefined, previousQuery: { queryKey: readonly unknown[] } | undefined) => {
    const previousProjectId =
      typeof previousQuery?.queryKey?.[2] === 'string' ? previousQuery.queryKey[2] : null;

    return shouldKeepStoryboardWindowPlaceholderData(previousProjectId, projectId)
      ? keepPreviousData(previousData)
      : undefined;
  };

export async function invalidateStoryboardBoardQueries(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  projectId: string | null | undefined
) {
  if (!projectId) return;

  await Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) => isStoryboardBoardProjectQuery(query.queryKey, 'list', projectId),
    }),
    queryClient.invalidateQueries({
      predicate: (query) => isStoryboardBoardProjectQuery(query.queryKey, 'skeleton', projectId),
    }),
    queryClient.invalidateQueries({
      predicate: (query) => isStoryboardBoardProjectQuery(query.queryKey, 'paginated', projectId),
    }),
  ]);
}

export async function invalidateStoryboardBoardContentQueries(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  projectId: string | null | undefined
) {
  if (!projectId) return;

  await Promise.all([
    queryClient.invalidateQueries({
      predicate: (query) => isStoryboardBoardProjectQuery(query.queryKey, 'list', projectId),
    }),
    queryClient.invalidateQueries({
      predicate: (query) => isStoryboardBoardProjectQuery(query.queryKey, 'paginated', projectId),
    }),
  ]);
}

/**
 * 获取项目的分镜看板骨架数据（轻量级）
 */
export function useStoryboardBoardSkeleton(
  projectId: string | null,
  trackTypes?: string[],
  startIndex?: number,
  endIndex?: number
) {
  const trackTypesKey = trackTypes?.length ? trackTypes.join(',') : 'default';
  const rangeKey =
    typeof startIndex === 'number' && typeof endIndex === 'number'
      ? `${startIndex}-${endIndex}`
      : 'all';
  return useQuery({
    queryKey: [...storyboardBoardKeys.skeleton(projectId!, trackTypesKey), rangeKey] as const,
    queryFn: () => storyboardBoardApi.getSkeleton(projectId!, trackTypes, startIndex, endIndex),
    enabled: !!projectId,
    staleTime: 5 * 60 * 1000, // 5 分钟
    gcTime: 10 * 60 * 1000, // 10 分钟
  });
}

/**
 * 获取项目的分镜看板分页数据（基于 orderIndex）
 */
export function useStoryboardBoardPaginated(
  projectId: string | null,
  startIndex: number,
  endIndex: number,
  trackTypes?: string[]
) {
  const trackTypesKey = trackTypes?.length ? trackTypes.join(',') : 'default';

  return useQuery({
    queryKey: storyboardBoardKeys.paginated(projectId!, startIndex, endIndex, trackTypesKey),
    queryFn: () => storyboardBoardApi.getPaginated(projectId!, startIndex, endIndex, trackTypes),
    enabled: !!projectId && startIndex >= 0 && endIndex >= startIndex,
    staleTime: 5 * 60 * 1000, // 5 分钟
    gcTime: 10 * 60 * 1000, // 10 分钟
    placeholderData: createStoryboardBoardPlaceholderData(projectId),
  });
}

/**
 * 获取项目的分镜看板数据
 */
export function useStoryboardBoard(projectId: string | null, trackTypes?: string[]) {
  const trackTypesKey = trackTypes?.length ? trackTypes.join(',') : 'all';
  return useQuery({
    queryKey: storyboardBoardKeys.list(projectId!, trackTypesKey),
    queryFn: async () => {
      const result = await storyboardBoardApi.getByProjectId(projectId!, trackTypes);
      return result as unknown as StoryboardBoardTrackWithItems[];
    },
    enabled: !!projectId,
  });
}

export function useStoryboardBoardWindow(
  projectId: string | null,
  trackTypes?: string[],
  startIndex?: number,
  endIndex?: number,
  options?: Pick<UseQueryOptions<StoryboardBoardPaginatedData>, 'enabled'>
) {
  const trackTypesKey = trackTypes?.length ? trackTypes.join(',') : 'all';
  const rangeKey =
    typeof startIndex === 'number' && typeof endIndex === 'number'
      ? `${startIndex}-${endIndex}`
      : 'all';

  return useQuery({
    queryKey: [...storyboardBoardKeys.list(projectId!, trackTypesKey), rangeKey] as const,
    queryFn: async () => {
      return storyboardBoardApi.getPaginated(
        projectId!,
        startIndex ?? 0,
        endIndex ?? 99,
        trackTypes
      );
    },
    enabled: (options?.enabled ?? true) && !!projectId,
    placeholderData: createStoryboardBoardPlaceholderData(projectId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/**
 * 更新分镜看板数据
 */
export function useUpdateStoryboardBoard() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      projectId,
      tracks,
    }: {
      projectId: string;
      tracks: StoryboardBoardTrackWithItems[];
    }) => storyboardBoardApi.update(projectId, tracks),
    onMutate: async ({ projectId, tracks }) => {
      await queryClient.cancelQueries({
        queryKey: storyboardBoardKeys.list(projectId),
      });

      const previousTracks = queryClient.getQueryData<StoryboardBoardTrackWithItems[]>(
        storyboardBoardKeys.list(projectId)
      );

      queryClient.setQueryData<StoryboardBoardTrackWithItems[]>(
        storyboardBoardKeys.list(projectId),
        tracks
      );

      return { previousTracks };
    },
    onError: (_error, { projectId }, context) => {
      if (context?.previousTracks) {
        queryClient.setQueryData(storyboardBoardKeys.list(projectId), context.previousTracks);
      }
    },
    onSuccess: (_data, { projectId }) => {
      clearStoryboardBoardCache(projectId);
      queryClient.invalidateQueries({
        queryKey: storyboardBoardKeys.list(projectId),
      });
    },
  });
}

/**
 * 添加分镜看板项目
 */
export function useAddStoryboardBoardItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      trackId,
      item,
    }: {
      projectId: string;
      trackId: string;
      item: StoryboardBoardItem;
    }) => {
      const tracks = queryClient.getQueryData<StoryboardBoardTrackWithItems[]>(
        storyboardBoardKeys.list(projectId)
      );
      if (!tracks) return null;

      const updatedTracks = tracks.map((track) => {
        if (track.id === trackId) {
          return {
            ...track,
            items: [...track.items, item],
          };
        }
        return track;
      });

      return storyboardBoardApi.update(projectId, updatedTracks);
    },
    onSuccess: (_, { projectId }) => {
      clearStoryboardBoardCache(projectId);
      queryClient.invalidateQueries({
        queryKey: storyboardBoardKeys.list(projectId),
      });
    },
  });
}

/**
 * 更新分镜看板项目
 */
export function useUpdateStoryboardBoardItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      trackId,
      itemId,
      updates,
    }: {
      projectId: string;
      trackId: string;
      itemId: string;
      updates: Partial<StoryboardBoardItem>;
    }) => {
      const tracks = queryClient.getQueryData<StoryboardBoardTrackWithItems[]>(
        storyboardBoardKeys.list(projectId)
      );
      if (!tracks) return null;

      const updatedTracks = tracks.map((track) => {
        if (track.id === trackId) {
          return {
            ...track,
            items: track.items.map((item) => (item.id === itemId ? { ...item, ...updates } : item)),
          };
        }
        return track;
      });

      return storyboardBoardApi.update(projectId, updatedTracks);
    },
    onSuccess: (_, { projectId }) => {
      clearStoryboardBoardCache(projectId);
      queryClient.invalidateQueries({
        queryKey: storyboardBoardKeys.list(projectId),
      });
    },
  });
}

/**
 * 删除分镜看板项目
 */
export function useDeleteStoryboardBoardItem() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      trackId,
      itemId,
    }: {
      projectId: string;
      trackId: string;
      itemId: string;
    }) => {
      const tracks = queryClient.getQueryData<StoryboardBoardTrackWithItems[]>(
        storyboardBoardKeys.list(projectId)
      );
      if (!tracks) return null;

      const updatedTracks = tracks.map((track) => {
        if (track.id === trackId) {
          return {
            ...track,
            items: track.items.filter((item) => item.id !== itemId),
          };
        }
        return track;
      });

      return storyboardBoardApi.update(projectId, updatedTracks);
    },
    onMutate: async ({ projectId, trackId, itemId }) => {
      await queryClient.cancelQueries({
        queryKey: storyboardBoardKeys.list(projectId),
      });

      const previousTracks = queryClient.getQueryData<StoryboardBoardTrackWithItems[]>(
        storyboardBoardKeys.list(projectId)
      );

      if (previousTracks) {
        const updatedTracks = previousTracks.map((track) => {
          if (track.id === trackId) {
            return {
              ...track,
              items: track.items.filter((item) => item.id !== itemId),
            };
          }
          return track;
        });
        queryClient.setQueryData<StoryboardBoardTrackWithItems[]>(
          storyboardBoardKeys.list(projectId),
          updatedTracks
        );
      }

      return { previousTracks };
    },
    onError: (_error, { projectId }, context) => {
      if (context?.previousTracks) {
        queryClient.setQueryData(storyboardBoardKeys.list(projectId), context.previousTracks);
      }
    },
    onSuccess: (_data, { projectId }) => {
      clearStoryboardBoardCache(projectId);
      queryClient.invalidateQueries({
        queryKey: storyboardBoardKeys.list(projectId),
      });
    },
  });
}

// ─── 缓存操作辅助函数（供其他 hook 调用，勿直接 import keys）───────────────

/**
 * 失活分镜看板**列表**缓存（仅 list 类型，不含 skeleton / paginated）。
 * 当只需刷新列表视图时使用，如 usePreviewActions 中的部分场景。
 */
export function invalidateStoryboardBoardLists(queryClient: QueryClient, projectId?: string): void {
  if (projectId) {
    void queryClient.invalidateQueries({
      predicate: (query) => isStoryboardBoardProjectQuery(query.queryKey, 'list', projectId),
    });
  } else {
    void queryClient.invalidateQueries({
      queryKey: storyboardBoardKeys.lists(),
    });
  }
}

/**
 * 失活所有分镜看板缓存（list + skeleton + paginated）。
 * 在删除/上传资源等影响全局看板的操作后使用。
 */
export function invalidateAllStoryboardBoardQueries(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: storyboardBoardKeys.all });
}

/**
 * 读取所有匹配的看板列表缓存数据（用于乐观更新前备份）。
 * 返回 [queryKey, data][] 元组数组，可在 onError 中用于回滚。
 */
export function getStoryboardBoardListsData(
  queryClient: QueryClient
): [QueryKey, StoryboardBoardTrackWithItems[] | undefined][] {
  return queryClient.getQueriesData<StoryboardBoardTrackWithItems[]>({
    queryKey: storyboardBoardKeys.lists(),
  });
}

/**
 * 批量更新所有匹配的看板列表缓存数据（用于乐观更新）。
 * updater 接收当前数据，返回新数据。
 */
export function setStoryboardBoardListsData(
  queryClient: QueryClient,
  updater: (
    old: StoryboardBoardTrackWithItems[] | undefined
  ) => StoryboardBoardTrackWithItems[] | undefined
): void {
  // list / window 查询可能存 tracks[]，也可能存 PaginatedData；需同时兼容
  queryClient.setQueriesData({ queryKey: storyboardBoardKeys.lists() }, (old) => {
    if (old == null) return old;
    if (Array.isArray(old)) {
      return updater(old as StoryboardBoardTrackWithItems[]);
    }
    const paginated = old as StoryboardBoardPaginatedData;
    if (!paginated?.tracks) return old;
    const nextTracks = updater(paginated.tracks);
    if (nextTracks === undefined) return old;
    return nextTracks === paginated.tracks ? old : { ...paginated, tracks: nextTracks };
  });
}

export function setStoryboardBoardDataByProject(
  queryClient: QueryClient,
  projectId: string,
  updater: (
    old: StoryboardBoardTrackWithItems[] | undefined
  ) => StoryboardBoardTrackWithItems[] | undefined
): void {
  queryClient.setQueriesData(
    {
      predicate: (query) => {
        const k = query.queryKey;
        return k[0] === 'storyboardBoard' && k[1] === 'list' && k[2] === projectId;
      },
    },
    (old) => {
      if (old == null) return old;
      if (Array.isArray(old)) {
        return updater(old);
      }
      const paginated = old as StoryboardBoardPaginatedData;
      const nextTracks = updater(paginated.tracks);
      if (nextTracks === undefined) return old;
      return { ...paginated, tracks: nextTracks };
    }
  );
}
